/* eslint-disable no-invalid-this */
import React, { useEffect, useState } from 'react';
import { FaCookieBite } from 'react-icons/fa';

import Button, { ButtonAppearance } from '../../../../ui/Atoms/Button';
import Icon from '../../../../ui/Atoms/Icon';
import Switch from '../../../../ui/Atoms/Switch';
import Text, { TextAppearance } from '../../../../ui/Atoms/Text';

import {
  addEventListener,
  CookieNames,
  getCookie,
  removeEventListener,
  setCookie,
} from '../../../utils/cookies';
import { gtm } from '../../../utils/gtm';
import {
  checkAndClearOldCookies,
  setTrackingCookies,
} from '../../../utils/trackingParams';

import { Color } from '../../../constants/Colors';
import * as S from './CookieBanner.styles';

const GTM_COOKIE_CONSENT_EVENT = 'cookie-consent';

export const CookieBanner = ({
  hideCookieBanner,
  localization,
  showCookieBanner,
}) => {
  const [state, setState] = useState({
    hasUpdated: false,
    hasCustomized: false,
    hasConsented: false,
    enableFunctionalityCookies: false,
    enablePerformanceCookies: false,
    enableTrackingCookies: false,
  });

  useEffect(() => {
    const { hasUpdated, hasConsented, hasCustomized, ...parsedConsentCookie } =
      getState();

    setState({ ...getState() });

    if (hasConsented === true && typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.gtag =
        window.gtag ||
        function () {
          dataLayer.push(arguments);
        };

      window.gtag('consent', 'default', {
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        ad_storage: 'denied',
        analytics_storage: parsedConsentCookie.enableTrackingCookies
          ? 'granted'
          : 'denied',
        functionality_storage: parsedConsentCookie.enableFunctionalityCookies
          ? 'granted'
          : 'denied',
        personalization_storage: parsedConsentCookie.enablePerformanceCookies
          ? 'granted'
          : 'denied',
        security_storage: 'denied',
      });

      gtm({
        event: GTM_COOKIE_CONSENT_EVENT,
        event_value: 'website',
        ...parsedConsentCookie,
        essential: true,
      });
    }

    addEventListener(onCookieChanged);

    return () => {
      removeEventListener(onCookieChanged);
    };
  }, []);

  const onCookieChanged = () => {
    setState(getState());
  };

  const getState = () => {
    const parsedConsentCookie = getParsedConsentCookie();
    const hasConsented = getHasConsented(parsedConsentCookie);

    return {
      hasUpdated: true,
      hasCustomized: false,
      hasConsented,
      enableFunctionalityCookies: parsedConsentCookie.functionality ?? false,
      enablePerformanceCookies: parsedConsentCookie.performance ?? false,
      enableTrackingCookies: parsedConsentCookie.tracking ?? false,
    };
  };

  const getParsedConsentCookie = () => {
    const rawConsentCookie = getCookie(CookieNames.CONSENT);
    return JSON.parse(
      typeof rawConsentCookie === 'string' &&
        ['true', '1'].includes(rawConsentCookie) === false
        ? rawConsentCookie
        : '{}'
    );
  };

  const getHasConsented = parsedConsentCookie =>
    typeof parsedConsentCookie.functionality === 'boolean' &&
    typeof parsedConsentCookie.performance === 'boolean' &&
    typeof parsedConsentCookie.tracking === 'boolean';

  const onCookieSwitch = scope => {
    const fieldName = `enable${scope[0].toUpperCase()}${scope.slice(1)}Cookies`;
    const { [fieldName]: currentValue } = state;

    if (typeof currentValue === 'boolean') {
      setState({ ...state, [fieldName]: !currentValue });
    }
  };

  const deleteCookies = cookiesToDelete => {
    const cookies = document.cookie.split(';');
    const hostnames = [
      location.hostname,
      location.hostname.startsWith('www')
        ? location.hostname.substring(4)
        : `www.${location.hostname}`,
    ];

    cookiesToDelete.forEach(cookieToDelete => {
      cookies.forEach(cookie => {
        const cookieName = cookie.split('=')[0].trim();
        if (
          (typeof cookieToDelete === 'string' &&
            cookieName === cookieToDelete) ||
          (cookieToDelete instanceof RegExp && cookieToDelete.test(cookieName))
        ) {
          hostnames.forEach(hostname => {
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${hostname};`;
            document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${hostname};`;
          });
        }
      });
    });
  };

  const setCookieConsent = ({
    enableFunctionalityCookies,
    enablePerformanceCookies,
    enableTrackingCookies,
  }) => {
    deleteCookies([
      'sib_cuid',
      '_ga',
      '_fbp',
      'origin',
      'first_page',
      'last_page',
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'utm_timestamp',
      'timestamp',
      'hubspotutk',
      '__hstc',
      '__hssrc',
      '__hssc',
      /_ga_/,
      /_gcl_/,
      /_hjSession_/,
      /_hjSessionUser_/,
    ]);

    const parsedConsentCookie = {
      functionality: enableFunctionalityCookies,
      performance: enablePerformanceCookies,
      tracking: enableTrackingCookies,
    };

    const rawConsentCookie = JSON.stringify(parsedConsentCookie);

    if (parsedConsentCookie.tracking) {
      checkAndClearOldCookies();
      setTrackingCookies();
    }

    setCookie(CookieNames.CONSENT, rawConsentCookie, 365, 'None');

    if (typeof window !== 'undefined') {
      window.gtag('consent', 'update', {
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        ad_storage: 'denied',
        analytics_storage: enableTrackingCookies ? 'granted' : 'denied',
        functionality_storage: enableFunctionalityCookies
          ? 'granted'
          : 'denied',
        personalization_storage: enablePerformanceCookies
          ? 'granted'
          : 'denied',
        security_storage: 'denied',
      });
    }

    gtm(
      {
        event: GTM_COOKIE_CONSENT_EVENT,
        event_value: 'website',
        ...parsedConsentCookie,
        essential: true,
      },
      afterCookieConsent
    );

    setState({ ...state, hasConsented: true });
    hideCookieBanner();
  };

  const afterCookieConsent = () => {
    // location.reload();
  };

  const onCookieConsent = evt => {
    evt.preventDefault();
    setCookieConsent(state);
  };

  const onCookieConsentEssential = evt => {
    evt.preventDefault();
    setCookieConsent({
      enableFunctionalityCookies: false,
      enablePerformanceCookies: false,
      enableTrackingCookies: false,
    });
  };

  const onCookieConsentAll = evt => {
    evt.preventDefault();
    setCookieConsent({
      enableFunctionalityCookies: true,
      enablePerformanceCookies: true,
      enableTrackingCookies: true,
    });
  };

  const onCookieCustomize = () => {
    setState({ ...state, hasCustomized: true });
  };

  const onCancel = () => {
    setState({ ...state, hasCustomized: false });
  };

  if ((state.hasConsented && !showCookieBanner) || !localization) {
    return null;
  }

  const {
    data: {
      cookie_button_reject: buttonReject,
      cookie_button_accept: buttonAccept,
      cookie_button_confirm: buttonConfirm,
      cookie_button_back: buttonBack,
      cookie_button_custom: buttonCustom,
      cookie_intro: { html: intro } = {},
      essential_label: essential,
      essential_description: essentialDescription,
      functional_label: functional,
      functional_description: functionalDescription,
      performance_label: performance,
      performance_description: performanceDescription,
      tracking_label: tracking,
      tracking_description: trackingDescription,
    } = {},
  } = localization || '';

  return (
    <>
      {state.hasUpdated && (
        <S.Modal>
          <Icon customColor={Color.WHITE} hasGradientBackground={true}>
            <FaCookieBite />
          </Icon>
          <Text type={TextAppearance.NORMAL}>{intro}</Text>
          {state.hasCustomized === true ? (
            <S.CustomizationContainer>
              <S.SwitchesWrapper>
                <S.Switch>
                  <Switch
                    isDefaultChecked={true}
                    isDisabled={true}
                    onClick={() => onCookieSwitch('essential')}
                  />
                  <S.SwitchDescription>{essential}</S.SwitchDescription>
                </S.Switch>
                <S.SwitchInfo>{essentialDescription}</S.SwitchInfo>
                <S.Switch>
                  <Switch
                    isDefaultChecked={state.enableFunctionalityCookies}
                    onClick={() => onCookieSwitch('functionality')}
                  />
                  <S.SwitchDescription>{functional}</S.SwitchDescription>
                </S.Switch>
                <S.SwitchInfo>{functionalDescription}</S.SwitchInfo>
                <S.Switch>
                  <Switch
                    isDefaultChecked={state.enablePerformanceCookies}
                    onClick={() => onCookieSwitch('performance')}
                  />
                  <S.SwitchDescription>{performance}</S.SwitchDescription>
                </S.Switch>
                <S.SwitchInfo>{performanceDescription}</S.SwitchInfo>
                <S.Switch>
                  <Switch
                    isDefaultChecked={state.enableTrackingCookies}
                    onClick={() => onCookieSwitch('tracking')}
                  />
                  <S.SwitchDescription>{tracking}</S.SwitchDescription>
                </S.Switch>
                <S.SwitchInfo>{trackingDescription}</S.SwitchInfo>
              </S.SwitchesWrapper>
              <S.CustomizationButtonWrapper>
                <Button
                  appearance={ButtonAppearance.OUTLINE}
                  onClick={onCancel}
                >
                  {buttonBack}
                </Button>
                <Button onClick={onCookieConsent}>{buttonConfirm}</Button>
              </S.CustomizationButtonWrapper>
            </S.CustomizationContainer>
          ) : (
            <S.ButtonWrapper>
              <Button
                appearance={ButtonAppearance.OUTLINE}
                onClick={onCookieConsentAll}
              >
                {buttonAccept}
              </Button>
              <Button
                appearance={ButtonAppearance.OUTLINE}
                onClick={onCookieConsentEssential}
              >
                {buttonReject}
              </Button>
              <Button
                appearance={ButtonAppearance.OUTLINE}
                onClick={onCookieCustomize}
              >
                {buttonCustom}
              </Button>
            </S.ButtonWrapper>
          )}
        </S.Modal>
      )}
    </>
  );
};
